import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { useToast } from '@/hooks/use-toast';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronRight, Globe, MapPin, Building2 } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';

interface CountryRow { id: string; code: string; name_translations: Record<string, string>; }
interface DistrictRow { id: string; code: string; name: string; total_cities: number; selected_cities: number; }
interface CityOption { id: string; code: string; name: string; is_selected: boolean; }

export default function CarrierCoverage() {
  const navigate = useNavigate();
  const { get, put, loading } = useApi();
  const { toast } = useToast();
  const { tp, lang } = useI18n();

  const [countries, setCountries] = useState<CountryRow[]>([]);
  const [districts, setDistricts] = useState<DistrictRow[]>([]);
  const [cities, setCities] = useState<CityOption[]>([]);

  const [selectedCountry, setSelectedCountry] = useState<CountryRow | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<DistrictRow | null>(null);
  const [selectedCityIds, setSelectedCityIds] = useState<Set<string>>(new Set());
  const [originalCityIds, setOriginalCityIds] = useState<Set<string>>(new Set());

  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    get(`/api/v1/settings/carrier-cities/countries?lang=${lang}`)
      .then((res: any) => setCountries(res || []))
      .catch(() => setCountries([]));
  }, []);

  const handleCountryClick = async (country: CountryRow) => {
    if (selectedCountry?.code === country.code) return;
    setSelectedCountry(country);
    setSelectedDistrict(null);
    setCities([]);
    setSelectedCityIds(new Set());
    setOriginalCityIds(new Set());
    setLoadingDistricts(true);
    try {
      const res: any = await get(`/api/v1/settings/carrier-cities/countries/${country.code}/states?lang=${lang}`);
      setDistricts(res || []);
    } catch {
      setDistricts([]);
    } finally {
      setLoadingDistricts(false);
    }
  };

  const handleDistrictClick = async (district: DistrictRow) => {
    if (selectedDistrict?.code === district.code) return;
    setSelectedDistrict(district);
    setCities([]);
    setSelectedCityIds(new Set());
    setOriginalCityIds(new Set());
    setLoadingCities(true);
    try {
      const res: any = await get(`/api/v1/settings/carrier-cities/states/${district.code}/cities?lang=${lang}`);
      const cityList: CityOption[] = res || [];
      setCities(cityList);
      const selected = new Set(cityList.filter((c) => c.is_selected).map((c) => c.id));
      setSelectedCityIds(new Set(selected));
      setOriginalCityIds(new Set(selected));
    } catch {
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  const toggleCity = (cityId: string) => {
    setSelectedCityIds((prev) => {
      const next = new Set(prev);
      if (next.has(cityId)) next.delete(cityId);
      else next.add(cityId);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedCityIds.size === cities.length) {
      setSelectedCityIds(new Set());
    } else {
      setSelectedCityIds(new Set(cities.map((c) => c.id)));
    }
  };

  const isDirty = (() => {
    if (selectedCityIds.size !== originalCityIds.size) return true;
    for (const id of selectedCityIds) if (!originalCityIds.has(id)) return true;
    return false;
  })();

  const handleSave = async () => {
    if (!selectedDistrict) return;
    setSaving(true);
    try {
      await put(`/api/v1/settings/carrier-cities/states/${selectedDistrict.code}`, {
        city_ids: Array.from(selectedCityIds),
      });
      setOriginalCityIds(new Set(selectedCityIds));
      toast({ title: tp.coverage.saved });
      // Refresh district counts
      if (selectedCountry) {
        const res: any = await get(`/api/v1/settings/carrier-cities/countries/${selectedCountry.code}/states?lang=${lang}`);
        setDistricts(res || []);
      }
    } catch {
      toast({ title: 'خطأ', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const allSelected = cities.length > 0 && selectedCityIds.size === cities.length;

  return (
    <PageContainer>
      <div className="mb-4 md:mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ChevronRight className="h-5 w-5" /></Button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold">{tp.coverage.pageTitle}</h1>
            <p className="text-sm text-muted-foreground">{tp.coverage.pageDescription}</p>
          </div>
        </div>
      </div>

      <div className="flex gap-0 h-[calc(100vh-180px)] border rounded-lg overflow-hidden">
        {/* Column 1: Countries */}
        <div className="w-1/3 border-e flex flex-col">
          <div className="px-4 py-3 border-b bg-muted/40 flex items-center gap-2">
            <Globe className="h-4 w-4 text-muted-foreground" />
            <span className="font-semibold text-sm">{tp.coverage.countryColumn}</span>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4"><LoadingSkeleton type="card" rows={4} /></div>
            ) : countries.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground text-sm gap-2 p-4">
                <Globe className="h-10 w-10 opacity-30" />
                <span>{tp.coverage.emptyCountries}</span>
              </div>
            ) : (
              countries.map((country) => (
                <button
                  key={country.code}
                  onClick={() => handleCountryClick(country)}
                  className={cn(
                    'w-full text-start px-4 py-3 text-sm border-b last:border-b-0 transition-colors hover:bg-accent',
                    selectedCountry?.code === country.code && 'bg-primary/10 font-semibold text-primary border-e-2 border-e-primary',
                  )}
                >
                  {country.name_translations[lang] || country.name_translations.en || country.code}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Column 2: Districts */}
        <div className="w-1/3 border-e flex flex-col">
          <div className="px-4 py-3 border-b bg-muted/40 flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <span className="font-semibold text-sm">{tp.coverage.districtColumn}</span>
          </div>
          <div className="flex-1 overflow-y-auto">
            {!selectedCountry ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground text-sm gap-2 p-4 text-center">
                <MapPin className="h-10 w-10 opacity-30" />
                <span>{tp.coverage.selectCountry}</span>
              </div>
            ) : loadingDistricts ? (
              <div className="p-4"><LoadingSkeleton type="list" rows={5} /></div>
            ) : (
              districts.map((district) => (
                <button
                  key={district.code}
                  onClick={() => handleDistrictClick(district)}
                  className={cn(
                    'w-full text-start px-4 py-3 text-sm border-b last:border-b-0 transition-colors hover:bg-accent',
                    selectedDistrict?.code === district.code && 'bg-primary/10 font-semibold text-primary border-e-2 border-e-primary',
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span>{district.name}</span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {district.selected_cities}/{district.total_cities}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Column 3: Cities */}
        <div className="w-1/3 flex flex-col">
          <div className="px-4 py-3 border-b bg-muted/40 flex items-center gap-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <span className="font-semibold text-sm">{tp.coverage.citiesColumn}</span>
          </div>
          <div className="flex-1 overflow-y-auto">
            {!selectedDistrict ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground text-sm gap-2 p-4 text-center">
                <Building2 className="h-10 w-10 opacity-30" />
                <span>{tp.coverage.selectDistrict}</span>
              </div>
            ) : loadingCities ? (
              <div className="p-4"><LoadingSkeleton type="list" rows={6} /></div>
            ) : (
              <>
                {cities.length > 0 && (
                  <div className="px-4 py-2 border-b flex items-center gap-3">
                    <Checkbox
                      id="select-all"
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                    />
                    <label htmlFor="select-all" className="text-xs text-muted-foreground cursor-pointer select-none">
                      {allSelected ? tp.coverage.deselectAll : tp.coverage.selectAll}
                    </label>
                  </div>
                )}
                {cities.map((city) => (
                  <div
                    key={city.id}
                    className="flex items-center gap-3 px-4 py-3 border-b last:border-b-0 hover:bg-accent/50 transition-colors"
                  >
                    <Checkbox
                      id={city.id}
                      checked={selectedCityIds.has(city.id)}
                      onCheckedChange={() => toggleCity(city.id)}
                    />
                    <label htmlFor={city.id} className="text-sm cursor-pointer select-none flex-1">
                      {city.name}
                    </label>
                  </div>
                ))}
              </>
            )}
          </div>
          {selectedDistrict && (
            <div className="p-4 border-t bg-background">
              <Button
                className="w-full"
                onClick={handleSave}
                disabled={!isDirty || saving}
              >
                {saving ? '...' : tp.coverage.saveBtn}
              </Button>
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
}
